import { toast } from "@/components/common/Toast/Toast"
import { Button } from "@/components/ui/button"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { cn } from "@/lib/utils"
import { QueryClientContext } from "@tanstack/react-query"
import { ArrowLeft, CheckCircle2, Maximize2, Minimize2 } from "lucide-react"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import {
  densePositionsOf,
  useCreateQuestion,
  useDeclarationQuestions,
} from "../../../../api/declarationQuestionsApi"
import { loadDeclarationScope } from "../../../../api/declarationsApi"
import { isPostApproval } from "../../../../api/lifecycle"
import type { ApproveResult, Declaration } from "../../../../api/types"
import { holdReasonOf } from "../../../shared/holdReason"
import { RoundtripHoldPill } from "../../../shared/RoundtripHoldPill"
import { isWorkbookFileName } from "../../declarationFileTypes"
import { DECLARATIONS_BASE, openSiblingDeclarationsPath } from "../../routes"
import { frameworkUnitId } from "../studio/cellState"
import { conditionSpec } from "../studio/conditions"
import { weiterTarget } from "../studio/serverWorklist"
import type { FilterToken } from "../studio/TableSearch"
import { buildWorkbookSources } from "../studio/workbookProofs"
import { DocumentPane, type EvidenceFlash } from "./document/DocumentPane"
import type { EvidencePick } from "./document/evidenceSelection"
import { ExtractionProgress } from "./document/ExtractionProgress"
import { MarkQuestionPopover } from "./document/MarkQuestionPopover"
import type { MarkCapture, PassageMark } from "./document/markSelection"
import {
  buildApprovedSnapshot,
  type ApprovedSnapshot,
} from "./done/approvedSnapshot"
import { DeclarationApprovedDialog } from "./done/DeclarationApprovedDialog"
import { loadPagedApprovedSnapshot } from "./done/pagedApprovedSnapshot"
import { StudioV2DoneScreen } from "./done/StudioV2DoneScreen"
import { BulkConformGesture } from "./footer/BulkConformGesture"
import { nextOpenDeclaration } from "./footer/nextOpenDeclaration"
import { nextOpenPoint, type OpenPointTarget } from "./footer/nextOpenPoint"
import type { RequestContext } from "./footer/requestContext"
import {
  StudioV2FooterBar,
  type StudioV2FooterBarHandle,
} from "./footer/StudioV2FooterBar"
import { useConformFramework } from "./footer/useConformFramework"
import { useStudioV2Keyboard } from "./keyboard/useStudioV2Keyboard"
import { conformBlockReason } from "./mapping/deriveFrameworkVerdict"
import { frameworkDisplayName } from "./mapping/frameworkDisplayName"
import type { CardRef } from "./mapping/pagedSummary"
import { InquiryCoverageHint } from "./inquiry/InquiryCoverageHint"
import { FrameworkNavigator } from "./navigator/FrameworkNavigator"
import type { ExternalEvidencePick } from "./reviewmask/AddStatementControl"
import { ExtractionHoldPanel } from "./reviewmask/ExtractionHoldPanel"
import { ReviewMaskPane, type MaskSheet } from "./reviewmask/ReviewMaskPane"
import { LinkAllUnresolvedSubstances } from "./reviewmask/UnresolvedSubstances"
import { nextDeclarationSearch } from "./studioV2Url"
import { SupplierAssignControl } from "./supplier/SupplierAssignControl"
import { SupplierGate } from "./supplier/SupplierGate"
import { SupplierReassignControl } from "./supplier/SupplierReassignControl"
import { useSupplierGateAssign } from "./supplier/useSupplierGateAssign"
import { DeclProgressBar } from "./topbar/DeclProgressBar"
import { HelpPopover, type HelpPopoverHandle } from "./topbar/HelpPopover"
import { usePagedGestures } from "./usePagedGestures"
import { type StatementCard, type StudioV2State } from "./useStudioV2State"

export interface StudioV2LayoutProps {
  supplierName: string
  state: StudioV2State
  declarations: Declaration[]
  /** C2 — THIS declaration's own row (`GET /{id}`), when the page opened from it
   *  (`MC_REVIEW_FAST_OPEN`) rather than from the list. When given it is the one
   *  source for everything about the active declaration (file name and viewer,
   *  approved / held panels, number); `declarations` then only feeds Next. */
  declaration?: Declaration
  /** B5 — the extraction has not finished yet (`isAwaitingExtraction` in
   *  `DeclarationReviewPage.tsx`, threaded through `StudioV2Page`). Governs
   *  which empty state the mask pane shows when `frameworks` is still empty:
   *  a waiting state while true, the final "no regulation on file" copy once
   *  it settles false. Defaults false so a caller (and every test predating
   *  this prop) that omits it keeps the final-copy behavior unchanged. */
  awaitingExtraction?: boolean
}

/** Every statement card on hand, in navigator order: flag off, every regulation's
 *  own; paged (C3-b3), the open regulation's live cards plus the others' as last
 *  loaded for a gesture (`usePagedGestures`). */
export function everyCardOnHand(
  frameworks: readonly string[],
  activeFramework: string | null | undefined,
  cardsByFramework: ReadonlyMap<string, StatementCard[]>,
  otherCards: ReadonlyMap<string, StatementCard[]> | undefined,
): StatementCard[] {
  return frameworks.flatMap((fw) =>
    otherCards && fw !== activeFramework
      ? (otherCards.get(fw) ?? [])
      : (cardsByFramework.get(fw) ?? []),
  )
}

/**
 * The full-bleed workspace shell — the prototype's own structure
 * (`renderReview()`): a flex column carrying `sv2-root`, `sv2-revtop`
 * (breadcrumbs + declaration progress + help), a draggable `sv2-rev3`
 * `ResizablePanelGroup` (navigator | review mask | document — the navigator
 * panel is OMITTED entirely below two Verordnungen, a single-Verordnung
 * declaration has nothing to navigate), a `sv2-kbhint` one-line keyboard
 * reminder, then `sv2-revfoot` (rendered by `StudioV2FooterBar` itself).
 *
 * DRAGGABLE, LIKE THE REST OF THE APP. An earlier fidelity pass replaced the
 * original `ResizablePanelGroup` with a fixed CSS grid pinned to the
 * prototype's exact column widths, reasoning that resize handles were "close
 * but not the design". The product owner then explicitly asked for the drag
 * behaviour back ("window sizes von dem splitscreen die dragable sind") —
 * every other split view in the app (`InquiryDraftPage`, `Layout`'s chat
 * dock, the master-data mapper) is a `ResizablePanelGroup`, and a workspace
 * this dense benefits from it more than most. The prototype's own column
 * widths survive as `defaultSize` (below) rather than as a hard `grid-
 * template-columns`, so the panes look byte-identical at their default sizes
 * and are draggable beyond that. TWO DISTINCT `autoSaveId`s (`sv2-panels-3`/
 * `sv2-panels-2`) — `react-resizable-panels` persists a saved layout keyed by
 * panel COUNT/ORDER, so a 2-pane and a 3-pane declaration sharing one id
 * would corrupt each other's saved sizes the moment a reviewer resizes one
 * and then opens the other shape. The ids carry ":v4" since
 * react-resizable-panels 4 reads v2-saved layouts back with sizes on the
 * wrong panes (v2 stored the ids alphabetised, sizes in screen order).
 *
 * ONCE APPROVED, THE WHOLE BODY SWAPS FOR `ApprovedView` (v1's own
 * component, reused verbatim — it is a pure leaf with no v1-only context,
 * see its own file) rather than any one pane doing so: an approved
 * declaration has no more open work for the panes or the footer to show,
 * mirroring how v1's `DeclarationReviewFlow` replaces its whole body the
 * same way. Once nowhere left to go (Übernehmen's "onDone", or
 * Überspringen, both find no next `ready` declaration) the body swaps AGAIN
 * for `StudioV2DoneScreen` (B11) — `showDone` is checked FIRST so it wins
 * once set, even though `approveResult` is still on hand from the run that
 * led there.
 */
export function StudioV2Layout({
  supplierName,
  state,
  declarations,
  declaration,
  awaitingExtraction = false,
}: StudioV2LayoutProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  // The overview's own list params (TUR-1211), carried forward so the "next
  // declaration" jump below walks the SAME order the reviewer saw there —
  // read raw rather than through `state.url` (`useStudioV2Url`'s own
  // contract owns only `sfw`/`stmt`/`zoom`/`at`).
  const [searchParams] = useSearchParams()
  const [approveResult, setApproveResult] = useState<ApproveResult | null>(null)
  // Read without `useQueryClient`, which throws outside a provider: only the paged
  // review (C3-b2) uses it, for reads it starts itself (the Anfordern pool, the
  // approved snapshot); a page always has a provider, a bare Layout test may not.
  const queryClient = useContext(QueryClientContext)
  // Paged (C3-b3): what the cross-regulation gestures load. Undefined flag off.
  const pagedGestures = usePagedGestures(queryClient, state)
  // WHY THIS CARRIES A REASON RATHER THAN A BOOLEAN. Two paths end on the done
  // screen and they mean different things: finishing a declaration and finding
  // no other open one ("completed"), versus SKIPPING and finding none loaded
  // ("no-more-loaded"). A skip persists nothing, so the declaration just skipped
  // is still open — announcing "all handled" there would be a plain lie.
  const [showDone, setShowDone] = useState<
    "completed" | "no-more-loaded" | null
  >(null)
  // MC_DECL_V2_SUP gate (product owner, 2026-09-04): a declaration that
  // reached review with no supplier bound cannot have its scope or verdicts
  // computed, so the mask is replaced entirely — never shown alongside a
  // banner, unlike the old unmatched-supplier treatment — until one is
  // assigned. `recomputing` stays true (and keeps blocking the mask) through
  // the post-assign re-extraction even after `supplierMatched` itself flips
  // true, see `useSupplierGateAssign`'s own "THE LATCH" doc for why that
  // extra beat matters.
  // TURNUS_MEMORY gates the read half only. Belt to the backend's braces: with the flag
  // off `memorySupplier` is already null because `get_extraction` never computed it, so
  // this is the second of two independent gates rather than the only one — a stale cached
  // response must not put a suggestion on screen for an org that has it switched off.
  const memoryEnabled = useFeatureFlag("TURNUS_MEMORY")
  // MC_SUPPLIER_REASSIGN (default off: off, the breadcrumb below is what it was).
  const supplierReassignEnabled = useFeatureFlag("MC_SUPPLIER_REASSIGN")
  // The reassignment is two requests, PUT /supplier (review) then /reextract
  // (retry), and approve refuses (SUPPLIER_CHANGED) until the second has run.
  // A reviewer who cannot re-extract never starts it.
  const { hasPermission } = useUser()
  const canReextract = hasPermission(PERMISSIONS.MC_DECLARATIONS_RETRY)
  // MC_SUPPLIER_MATCH_STRICT gates the gate's similar-name list, the same two-gate shape
  // as TURNUS_MEMORY above: the backend already sends it empty with the flag off.
  const supplierMatchStrict = useFeatureFlag("MC_SUPPLIER_MATCH_STRICT")

  const { recomputing, handleAssigned } = useSupplierGateAssign(
    state.declId,
    awaitingExtraction,
  )
  const needsSupplierGate =
    state.supplierMatched === false && !awaitingExtraction
  const showSupplierGate = recomputing || needsSupplierGate
  const { frameworks, activeFramework } = state
  // Focus mode (design spec "Entry and exit"): a layout flag on this SAME
  // screen, not a second route. Read once here since chrome (crumbs,
  // navigator, panel `autoSaveId`), the escape chain and the entering-focus
  // advance below all branch on it.
  const focus = state.url.state.focus
  const activeCards = activeFramework
    ? (state.statementCardsByFramework.get(activeFramework) ?? [])
    : []
  // Read once here (rather than at its pre-existing call site below) because
  // `isWorkbook` needs it FIRST — see `isWorkbook`'s own doc for why. Still
  // the SAME single read the guard/panel prop below narrow off of.
  const activeDeclaration =
    declaration ?? declarations.find((d) => d.id === state.declId)
  // The one derived hold reason (blank round-trip template or unfilled
  // customer form), see `holdReasonOf`'s own doc for why both fold into a
  // single answer here rather than two separate checks.
  const holdReason = activeDeclaration ? holdReasonOf(activeDeclaration) : null
  // The "change supplier" control: only on a declaration the backend will
  // accept a reassignment for (`assign_supplier` 409s NOT_PROCESSED while the
  // pipeline runs and ALREADY_APPROVED afterwards). `lifecycleStatus ===
  // "ready"` is exactly processed-and-not-approved: pending/processing are
  // the wait, failed never reaches this layout, and accepted/expiring/expired
  // are post-approval. A held copy has nothing staged to re-evaluate, and the
  // latch of an assignment already in flight (`recomputing`) must not be
  // stacked with a second one.
  const canReassignSupplier =
    supplierReassignEnabled &&
    canReextract &&
    hasPermission(PERMISSIONS.MC_DECLARATIONS_REVIEW) &&
    state.supplierMatched === true &&
    activeDeclaration?.lifecycleStatus === "ready" &&
    !holdReason &&
    !recomputing &&
    !approveResult
  // WHICH VIEWER THE DOCUMENT PANE OPENS. The file's own name is the PRIMARY
  // signal, ORed with the extraction read model's `document_kind` rather than
  // gating on one or the other: the read model is simply ABSENT until a run
  // has completed at least once, so a fresh upload or a re-evaluating
  // declaration used to fall through this check's old
  // `state.readModel?.document_kind === "workbook"` form to `false`, opening
  // the PDF viewer on a workbook's raw bytes ("Could not load the PDF.",
  // reported live for `hummel_erp.xlsx`). `activeDeclaration` comes from the
  // declarations LIST query, which knows the file name independently of
  // extraction, so this stays true through pending/processing/re-evaluating/
  // failed alike — and `||`, not a ternary that stops consulting the read
  // model the moment `activeDeclaration` exists, keeps two edge cases safe: a
  // `doc` with an unrecognized extension, and a completed run whose read
  // model actually knows better than a mislabeled display name.
  const isWorkbook =
    isWorkbookFileName(activeDeclaration?.doc) ||
    state.readModel?.document_kind === "workbook"
  // The "Aussage {i}" label every OTHER evidence surface uses — reused
  // verbatim (see `handleJumpToEvidence`'s own PDF branch below) as
  // `buildWorkbookSources`' `titleOf`, so a workbook mark's label matches
  // whatever the card itself shows, exactly like v1's `flow.titleOf` did for
  // its own (differently-derived) statement titles.
  const titleOf = (statementId: string) => {
    const i = activeCards.findIndex((c) => c.id === statementId)
    return translate("MC_DECL_V2_UNIT_LABEL", { i: i + 1 })
  }
  // Every regulation's statements in card order (id, group index, excluded). Paged
  // (C3-b2): the open regulation from its cards, the others from the server's
  // summary; flag off: the cards themselves, exactly as before.
  const cardRefsOf = (fw: string): readonly CardRef[] =>
    state.cardRefsByFramework?.get(fw) ??
    state.statementCardsByFramework.get(fw) ??
    []
  // TUR-1211 (W2-3): `OpenPointsPopover` names a statement on ANY framework,
  // not just the active one, so it needs a version of `titleOf`'s own lookup
  // that is not scoped to `activeCards`. Reads the SAME
  // `statementCardsByFramework` the cards themselves render from, so the
  // popover's "Aussage N" can never disagree with what the card list shows
  // at that position — falls back to the statement's own `groupIndex` only
  // for a miss that should not happen (the two share one source).
  const labelOf = (
    framework: string,
    statementId: string,
    groupIndex: number | undefined,
  ): number => {
    const cards = cardRefsOf(framework)
    const i = cards.findIndex((c) => c.id === statementId)
    // Miss ⇒ the SAME fallback the popover and gate panel default to, so a
    // statement absent from the card map never reads "Aussage 0".
    return i === -1 ? (groupIndex ?? 0) + 1 : i + 1
  }
  // DISPLAY order per framework (TUR-1211 W2-3b) — the same card list
  // `labelOf` above reads, handed to BOTH Weiter (`nextOpenPoint`) and the
  // open-points map, so the two walk and list one order.
  const cardOrderOf = (fw: string) => cardRefsOf(fw).map((c) => c.id)
  // The active framework's cell proofs, resolved once per framework — the
  // Studio V2 twin of v1 StudioScreenA's own `workbookSources` memo (see
  // that file ~304-321). `[]` on a PDF declaration, or before any active
  // card carries cell evidence. `surfaces` comes straight off the read
  // model B9 already fetches (`state.readModel`) — no new query.
  const workbookSources = useMemo(
    () =>
      isWorkbook
        ? buildWorkbookSources(
            activeCards.map((c) => ({ id: c.id, evidence: c.cells ?? [] })),
            titleOf,
            state.readModel?.surfaces,
          )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isWorkbook, activeCards, state.readModel?.surfaces],
  )
  const [jumpToPage, setJumpToPage] = useState<{
    page: number
    token: string
  } | null>(null)
  // A plain counter, not `Date.now()`: two clicks on the SAME evidence page
  // inside one millisecond (a fast double-click) must still be two distinct
  // tokens, or `DeclarationDocumentViewer` sees no change and re-scrolls
  // nothing the second time.
  const jumpToken = useRef(0)
  // The unit card's evidence flash (D-evidence) — see `EvidenceFlash`'s own
  // doc on `DocumentPane`. A separate piece of state from `jumpToPage`
  // (rather than folding the two together) because they answer different
  // questions: `jumpToPage` is "which page", `evidenceFlash` is "which quote,
  // and whose" — a statement can carry evidence with no resolvable quote at
  // all (a workbook card, or a legacy run), in which case only the page jump
  // fires and this stays null.
  const [evidenceFlash, setEvidenceFlash] = useState<EvidenceFlash | null>(null)
  // The sheet standing in for the mask's cards (the article browser, round 3;
  // the substance table, round 6) and the document pane's find bar, both
  // owned HERE rather than by the pane that renders them: Esc must close the
  // sheet before it leaves the workspace (`useStudioV2Keyboard`'s `onEscape`
  // below), a framework switch must close it (the effect below, alongside
  // the stale-flash clear), and the browser's table is filtered by the SAME
  // find-bar expression the document pane owns, one search field for both
  // panes, so the expression cannot live in either pane alone.
  const [maskSheet, setMaskSheet] = useState<MaskSheet | null>(null)
  const [findTokens, setFindTokens] = useState<FilterToken[]>([])
  const [findSearch, setFindSearch] = useState("")
  // TUR-1211 wave 5 (L8): the find bar's own open/closed flag, owned HERE
  // rather than by `DocumentPane` (which used to hold it in a local
  // `useState`): a reviewer-closed bar must stay closed across whatever this
  // pane's own lifecycle does underneath it (a remount is exactly the kind
  // of thing a local `useState` cannot survive, this state can). Closed by
  // default; the only writers are `requestFindFocus` below and
  // `handleJumpToEvidence`'s own non-auto branch: an AUTOMATIC flash (fired
  // by the effect that runs on mount and on every framework/statement
  // change) never touches it, which is the whole fix: the bar no longer
  // opens itself.
  const [barOpen, setBarOpen] = useState(false)
  // Live-test finding L7 (wave 5): the browser is filtered by the expression
  // above with no box of its own naming it (an empty table read as a bug,
  // the rail still counting the real pool). `ArticleBrowser`'s own filter
  // chip clears it through THESE SAME setters, never a second, browser-
  // local clear path, so the document pane's bar and the browser's chip
  // always agree on the one shared expression.
  const handleClearFind = () => {
    setFindTokens([])
    setFindSearch("")
  }
  // Closing the bar (its ✕ or Escape, the only closers) ends the search: a
  // hidden expression kept filtering the article browser with no visible
  // search box, so the reviewer reopens to an empty bar.
  const handleBarOpenChange = (open: boolean) => {
    setBarOpen(open)
    if (!open) handleClearFind()
  }
  // The article browser's search icon: the find bar is DocumentPane's, on
  // the other side of the screen, so the request travels as a counter the
  // pane watches (focuses the input, even while already open) rather than
  // as a ref reaching across panes. R10: this is a genuine user action (a
  // click), so it also opens the bar itself, right here, in the same write
  // as the nonce bump: one commit, not a `DocumentPane` effect reacting to
  // the nonce a render later.
  const [findFocusNonce, setFindFocusNonce] = useState(0)
  const requestFindFocus = () => {
    setFindFocusNonce((n) => n + 1)
    setBarOpen(true)
  }
  // The workbook twin of `evidenceFlash` — a cell proof IS an address, so a
  // click names the mark BY KEY rather than a quote to hunt for (mirrors v1
  // StudioScreenA's own `workbookFocus`, see its module doc). `statementId`
  // rides alongside the key so `DocumentPane` can also thread the viewer's
  // own `activeStatementId` (which statement's proofs stay tinted) — the
  // key alone only lands the ◀▶ cursor, it does not select the statement.
  const [workbookFocus, setWorkbookFocus] = useState<{
    key: string
    statementId: string
  } | null>(null)
  // A reader hint's sheet row ("Auszufüllen · Zeile 766"), the row-addressed
  // sibling of `workbookFocus`: a hint is no proof, so it names a row rather
  // than a mark, and it never selects a statement. A fresh object per click,
  // so the same line lands twice. Dropped when the view moves to a proof or
  // to another Verordnung, so a stale row tint never lingers.
  const [workbookRowFocus, setWorkbookRowFocus] = useState<{
    sheet: string
    row: number
  } | null>(null)
  const handleJumpToSheetRow = (sheet: string, row: number) =>
    setWorkbookRowFocus({ sheet, row })
  // The viewer's own ◀▶ (a proof or a search hit) never passes through
  // `handleJumpToEvidence`, so it reports the step back here instead.
  const handleWorkbookRowFocusEnd = () => setWorkbookRowFocus(null)
  const handleJumpToEvidence = (
    card: StatementCard,
    index: number,
    evidenceIndex = 0,
    // L8: true for the auto-flash effect below (fires on mount and on every
    // framework/statement change, with no gesture behind it), false for
    // every real caller (a card click, `onJumpToEvidence`'s own default),
    // which is why every existing call site is untouched. Gates whether this
    // jump also opens the bar: an automatic flash must never pop the bar
    // open by itself, but the reviewer still needs to SEE the flashed quote
    // once they open it themselves, so the flash/focus state below is set
    // identically either way, only the bar-open write is conditional.
    auto = false,
  ) => {
    if (isWorkbook) {
      // The card's FIRST cell proof — the same "first anchor" convention
      // `evidencePage`/`highlight` use below for a PDF card. A card with no
      // resolvable proof in `workbookSources` (cells present but none of
      // `kind: "cell"`, an edge case the read model does not actually
      // produce) is a silent no-op rather than a crash: there is nowhere to
      // jump to.
      const source = workbookSources.filter((s) => s.statementId === card.id)[
        evidenceIndex
      ]
      if (source) {
        setWorkbookFocus({ key: source.key, statementId: card.id })
        setWorkbookRowFocus(null)
        if (!auto) setBarOpen(true)
      }
      return
    }
    const selected = card.evidence[evidenceIndex] ?? card.evidence[0]
    if (selected) {
      jumpToken.current += 1
      setJumpToPage({
        page: selected.page,
        token: `${selected.page}:${jumpToken.current}`,
      })
    }
    if (card.evidence.length === 0) {
      setEvidenceFlash(null)
      return
    }
    // The SAME "Aussage {i}" label the card itself shows — reused verbatim
    // (existing key, no new string) rather than the raw statement id, which
    // would otherwise leak a bare UUID into the PowerBar's picker/ruler
    // tooltip once the bar opens (`SourceRegion.label`'s own doc: "the
    // statement title").
    const label = translate("MC_DECL_V2_UNIT_LABEL", { i: index + 1 })
    // One `SourceRegion` per evidence anchor, all under this ONE statement —
    // gives the fallback bbox box (`DeclarationDocumentViewer::boxesByPage`)
    // a source to key off for every anchor, not just the one the `highlight`
    // itself flashes. `colorIndex` is a fixed 0: only one statement's sources
    // are ever live at a time (cleared below on a framework switch, and by
    // every OTHER card's own click), so there is never a second colour to
    // tell it apart from.
    const sources = card.evidence.map((e, i) => ({
      key: `${card.id}:${i}`,
      statementId: card.id,
      label,
      colorIndex: 0,
      page: e.page,
      quote: e.quote,
      bbox: e.bbox ?? null,
      occurrence: e.occurrence ?? undefined,
    }))
    setEvidenceFlash({
      highlight: {
        page: selected.page,
        quote: selected.quote,
        key: sources[evidenceIndex]?.key ?? sources[0].key,
        occurrence: selected.occurrence ?? undefined,
      },
      sources,
      activeStatementId: card.id,
    })
    if (!auto) setBarOpen(true)
  }
  // STALE HIGHLIGHTS MUST NOT LINGER across a framework switch — the flash
  // above is keyed to ONE statement of the PREVIOUSLY active Verordnung, and
  // the shared `DocumentPane` spans every Verordnung (unlike v1's per-screen
  // viewer instance, which never faced this: switching its own "regulation"
  // concept there also unmounted or explicitly cleared `highlight`, see e.g.
  // `StudioScreenB`'s `setHighlight(null)` on its own mode toggle). Without
  // this, a quote from "reach" would keep glowing yellow over "rohs"'s pages.
  //
  // DELIBERATELY LEAVES THE SEARCH BAR OPEN. `barOpen` (this file's own
  // state, L8) is untouched here: this only clears the flash, matching v1's OWN
  // `setHighlight(null)` call sites (`StudioScreenA`/`StudioScreenB`'s mode
  // toggles), neither of which closes the bar either. The bar is a
  // an independent affordance from any one highlight; closing it out from under
  // a reviewer who opened it deliberately (⌘F, or the floating toggle) would
  // be a second, unrelated side effect this clear has no business causing.
  // Declared BEFORE the auto-flash effect below on purpose: React runs a
  // component's effects in declaration order, so a framework switch clears
  // (null + ref reset) and THEN auto-flashes the new framework's card in the
  // same commit — the pane never renders a null frame in between.
  const autoFlashedTarget = useRef<string | null>(null)
  // Which Verordnung the CURRENT `maskSheet` (if any) was opened for, set by
  // `handleMaskSheetChange` itself. A step-2/3 jump that ALSO switches
  // Verordnung opens its sheet from the freshly mounted `UnitCard`'s own
  // focus effect, in the SAME commit as this effect below: React fires
  // child effects and parent effects in the same commit in an unspecified
  // relative order for two DIFFERENT components, so an unconditional
  // `setMaskSheet(null)` here could win a race against that mount effect
  // and silently close a sheet the jump just asked for. Comparing against
  // this ref instead of clearing blindly is correct either way round: if
  // this effect runs first, the ref still names the OLD Verordnung, so it
  // clears (correctly, nothing has opened yet) and the mount effect's own
  // `setMaskSheet` right after is unaffected; if the mount effect runs
  // first, the ref already names the NEW Verordnung and this effect skips
  // the clear entirely.
  const maskSheetFramework = useRef<string | null>(null)
  useEffect(() => {
    setEvidenceFlash(null)
    setWorkbookFocus(null)
    setWorkbookRowFocus(null)
    autoFlashedTarget.current = null
    // The sheet was opened for a statement of the PREVIOUS Verordnung;
    // the new one's cards are what the reviewer should land on. Never a
    // sheet a same-commit jump just opened FOR this Verordnung (see the
    // ref's own doc above).
    if (maskSheetFramework.current !== state.activeFramework) setMaskSheet(null)
    // The find EXPRESSION, not the bar's open/closed state (left alone,
    // same reasoning as the comment above): a "Pb" search left over from
    // "reach" would silently filter "rohs"'s browser/table the reviewer
    // never asked to filter.
    setFindTokens([])
    setFindSearch("")
    // A pending mark popover names a target from `markTargets`, which spans
    // EVERY framework (see that const's own comment), so it would happily
    // let the reviewer re-target a quote/page/boxes captured off the OLD
    // framework's document at a statement in the NEW one (review finding F4,
    // wave 5). Closing it here is the same "previous Verordnung's leftover
    // UI must not survive" reasoning as `maskSheet`/find above.
    setPendingMark(null)
  }, [state.activeFramework])

  const footerRef = useRef<StudioV2FooterBarHandle>(null)
  const helpRef = useRef<HelpPopoverHandle>(null)

  // The guided layer (Weiter/W + the open-points popover wiring) is standard
  // Studio V2 behavior: the handler and showNextShortcut are always passed.
  // Children keep these props optional, so nothing downstream changed shape.
  // Bumped on EVERY jump (Weiter and the open-points popover alike) so
  // `ReviewMaskPane`'s focus effect re-runs even when the URL patch names
  // the statement it already shows — the pre-existing silent no-op both
  // paths shared. Not a URL nonce (the URL stays shareable), not a direct
  // DOM call (a cross-framework target is not mounted until after the
  // patch renders).
  const [focusNonce, setFocusNonce] = useState(0)
  // THE FIRST STATEMENT'S EVIDENCE IS SHOWN WITHOUT A CLICK (TUR-1211, Erik
  // 09.09): on opening a Verordnung the document pane used to sit on page 1
  // with nothing marked until the reviewer clicked a card, so every
  // declaration cost one extra click per statement before the PDF showed
  // anything. This fires the SAME jump a card click would (page scroll +
  // quote flash + tinted sources, or the workbook cell focus) for the URL's
  // focused statement when there is one (a Weiter/deep-link target), else
  // the first non-excluded card. A framework-UNIT id in the URL (what a
  // framework-kind open point writes) matches no card and falls through to
  // that first card too.
  //
  // Once per FOCUS TARGET (framework + url statement id, the ref), not per
  // framework: Weiter within one Verordnung patches only `url.statementId`,
  // and the PDF must follow it (review finding, this PR). A manual card
  // click never touches the URL, so a later refetch that re-derives the same
  // cards still never yanks the pane away from what the reviewer clicked.
  // `focusNonce` rides in the deps so a Weiter to the id already focused
  // re-fires exactly like `ReviewMaskPane`'s own focus effect does. Gated on
  // the card's id, not the array, because cards arrive async after the
  // framework is set.
  const autoFlashCard =
    activeCards.find((c) => c.id === state.url.state.statementId) ??
    activeCards.find((c) => !c.excluded) ??
    null
  const autoFlashTarget = `${activeFramework ?? ""}:${state.url.state.statementId ?? ""}:${focusNonce}`
  useEffect(() => {
    if (!activeFramework || !autoFlashCard) return
    if (autoFlashedTarget.current === autoFlashTarget) return
    autoFlashedTarget.current = autoFlashTarget
    // L8: `auto: true`, no gesture behind this call, so it must not pop the
    // find bar open on a fresh load, a framework switch, or a Weiter jump.
    handleJumpToEvidence(
      autoFlashCard,
      activeCards.indexOf(autoFlashCard),
      0,
      true,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFlashTarget, autoFlashCard?.id])
  const goToOpenPoint = (target: OpenPointTarget) => {
    if (target.kind === "approve") {
      footerRef.current?.focusApprove()
      return
    }
    // A step jump (the open-points popover, the approve gate) must land on a
    // MOUNTED card: the mask sheet (article browser or substance table)
    // replaces the cards entirely while it is open (`ReviewMaskPane`'s
    // ternary), so a jump that landed while some OTHER statement's sheet
    // was open would patch the URL and bump the focus nonce for a card that
    // is not even in the DOM. Closing it here, before the patch, is
    // symmetric with the framework-switch effect above, which does the same
    // for the same reason.
    setMaskSheet(null)
    setFocusNonce((n) => n + 1)
    state.url.patch({
      framework: target.framework,
      // The &stmt= param addresses the UNIT (see WorklistIssue's doc): a
      // framework jump writes the framework UNIT id, not null — nulling it
      // made the framework target an absorbing state for Weiter (the next
      // press found no current position and returned the same target
      // forever; claude-review, this PR). `focusUnit` ignores an id with no
      // card anchor, so the mask is unaffected.
      statementId:
        target.kind === "statement" ? target.statementId : target.unitId,
      // W5-4: the blocking step a direct caller named (the approve gate,
      // the open-points popover), routed through `UnitCard`'s own
      // `focusStep` via `ReviewMaskPane`'s `focusedStep` below, opens that
      // step's dialog on the landed card. ALWAYS written (never omitted): Weiter's own
      // targets never carry a step (`nextOpenPoint`'s own doc), and a
      // stale step from an EARLIER jump must not silently reattach itself
      // to a later, unrelated one.
      step: target.kind === "statement" ? (target.step ?? null) : null,
      at: "mask",
    })
  }
  const handleNextOpenPoint = () => {
    // C1-d: on a missing or failed server answer Weiter stays on the current card
    // rather than pointing at an Approve that stays disabled (`weiterTarget`).
    const target = weiterTarget(
      nextOpenPoint({
        worklist: state.worklist,
        frameworks,
        activeFramework,
        focusedStatementId: state.url.state.statementId ?? null,
        cardOrderOf,
      }),
      state.openItemsStatus,
    )
    if (target) goToOpenPoint(target)
  }

  // TUR-1445's selection chip wiring: a selection confirmed on the PDF
  // becomes an ExternalEvidencePick for the add-statement dialog — which
  // opens around it (selection-first), or swallows it as its Fundstelle when
  // it already stands open (the dialog is non-modal for exactly this).
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const pickToken = useRef(0)
  const [externalPick, setExternalPick] = useState<ExternalEvidencePick | null>(
    null,
  )
  const handleEvidencePick = (pick: EvidencePick) => {
    pickToken.current += 1
    setExternalPick({ pick, token: pickToken.current })
  }

  // TUR-1211 (W5-1): passage marking mirrors the evidence-pick wiring above:
  // a toggle in the document toolbar, a selection while it is on becomes a
  // question item instead of an evidence pick. The item is attached to the
  // FOCUSED statement, `state.url.state.statementId`, the same URL-driven
  // focus `goToOpenPoint`/`autoFlashTarget` already read, valid for both a
  // PDF and a workbook declaration (`workbookFocus` only ever carries a
  // value in the workbook case), rather than opening a second dialog, so
  // marking stays a one-click action once a card is focused.
  const [markMode, setMarkMode] = useState(false)
  // TUR-1211 fix 3h: mark mode and the mask sheet (article browser or
  // substance table) are mutually exclusive over the same document pane.
  // Opening either sheet kind turns marking off here; the toggle is ALSO
  // disabled for as long as a sheet stays open (`articleBrowserOpen` on
  // `DocumentPane` below), so the reviewer cannot turn it back on from
  // underneath the sheet either.
  const handleMaskSheetChange = (sheet: MaskSheet | null) => {
    setMaskSheet(sheet)
    // See `maskSheetFramework`'s own doc: records which Verordnung a FRESH
    // sheet belongs to, so a same-commit framework switch never races it.
    if (sheet) maskSheetFramework.current = activeFramework
    if (sheet) setMarkMode(false)
    // Review finding (wave 5): a step-2 or step-3 open-point jump
    // (`goToOpenPoint`) opens a sheet through UnitCard's own focus effect
    // and leaves `step=2`/`step=3` standing in the URL. The sheet REPLACES
    // the cards while open (this file's ternary below), so closing it
    // (Cancel/Apply for the article browser, Fertig/Esc for the substance
    // table, all routing through this same `null` call) remounts them;
    // UnitCard's focus effect then re-runs on mount with the stale
    // `focusStep` still in the URL and reopens the sheet immediately, so
    // closing it could never actually leave it standing closed. Clearing
    // the step here, only on close, breaks that loop without touching the
    // jump itself (`goToOpenPoint` still writes the step so the sheet opens
    // in the first place).
    if (!sheet) state.url.patch({ step: null })
  }
  const { data: questionDrafts } = useDeclarationQuestions(state.declId, false)
  const createQuestion = useCreateQuestion(state.declId)
  const focusedStatementId = state.url.state.statementId ?? null
  // Dense off ALL drafts, not just the ones with boxes below: a free-text
  // question still occupies a slot in the same numbering the mail's list
  // block counts against, so a badge's number must be its rank among every
  // draft (live test L5, wave 5: a deleted question left a gap in the
  // stored `position` values, and the badges echoed it instead of
  // renumbering densely like the mail body already does).
  const questionPositions = densePositionsOf(questionDrafts ?? [])
  const marks: PassageMark[] = (questionDrafts ?? [])
    .filter((q) => q.boxes && q.boxes.length > 0)
    .map((q) => ({
      n: questionPositions.get(q.id) ?? q.position,
      page: q.page ?? 0,
      quote: q.quote ?? "",
      boxes: q.boxes ?? [],
      question: q.question,
    }))
  // TUR-1211 (W5-1): the mark's target card is a choice, not an assumption:
  // a selection while marking opens a popover naming every statement on the
  // declaration (every framework, not just the active one, since a reviewer
  // may be marking a passage that answers a DIFFERENT Verordnung's open
  // question while reading the same page). The focused card preselects;
  // absent a focus, the active framework's first statement does.
  //
  // W5 overflow fix: the statement label ("Aussage N") comes FIRST, the
  // framework name second: in the popover's 320px-wide native `<select>`
  // (`MarkQuestionPopover`) a long framework name pushed "Aussage 1"/
  // "Aussage 2" past the visible width, so two different targets read
  // identically. Both labels are DISPLAY ONLY: the selection itself keys on
  // `framework`/`statementId`, never on this string, so reordering it here
  // changes nothing about which statement gets picked.
  const markTargets = frameworks.flatMap((fw) =>
    cardRefsOf(fw).map((c, i) => ({
      framework: fw,
      statementId: c.id,
      label: `${translate("MC_DECL_V2_UNIT_LABEL", { i: i + 1 })} · ${frameworkDisplayName(fw, state.regulationNameByFramework, translate)}`,
    })),
  )
  const [pendingMark, setPendingMark] = useState<{
    mark: MarkCapture
    anchor: { x: number; y: number }
    framework: string
    statementId: string
    question: string
  } | null>(null)
  const handleMark = (mark: MarkCapture, anchor: { x: number; y: number }) => {
    if (markTargets.length === 0) return
    const focused =
      activeFramework && focusedStatementId
        ? markTargets.find(
            (t) =>
              t.framework === activeFramework &&
              t.statementId === focusedStatementId,
          )
        : undefined
    const fallback =
      markTargets.find((t) => t.framework === activeFramework) ?? markTargets[0]
    const target = focused ?? fallback
    setPendingMark({
      mark,
      anchor,
      framework: target.framework,
      statementId: target.statementId,
      question: "",
    })
  }
  const confirmPendingMark = () => {
    // Mirrors the popover's own disabled Hinzufügen: the backend rejects an
    // empty question, so never POST one.
    if (!pendingMark || !pendingMark.question.trim()) return
    createQuestion.mutate(
      {
        framework: pendingMark.framework,
        statementId: pendingMark.statementId,
        page: pendingMark.mark.page,
        quote: pendingMark.mark.quote,
        boxes: pendingMark.mark.boxes,
        question: pendingMark.question.trim(),
      },
      {
        // Only a SUCCESSFUL create closes the popover: a failed POST must
        // never silently lose the marked passage and the typed question
        // (review, wave 5), so the popover stays open on error.
        //
        // NO `onError` here. React Query runs a per-call handler IN ADDITION
        // to the mutation's own, and `useCreateQuestion` already toasts — and
        // does it knowing whether the refusal was the disabled feature or
        // anything else. A second, generic toast beside it contradicted the
        // one cause the reviewer had just been given.
        onSuccess: () => setPendingMark(null),
      },
    )
  }
  // A SEPARATE `useConformFramework` instance from `ReviewMaskPane`'s own —
  // both are thin wrappers over the same mutations (react-query dedupes the
  // underlying reads), and keeping Enter's keyboard trigger independent of
  // the button's local `busy` state means one cannot block the other.
  const { conformFramework } = useConformFramework(state.declId)

  // Hidden while the supplier gate stands: the navigator's own geprüft
  // checkmarks and R1-R4 badges are verdict-derived (`derivationOf`,
  // `state.geprueft`) off the SAME pre-supplier statements the gate exists
  // to stop the reviewer from trusting — showing it here would be the exact
  // "misleading without a supplier" problem the gate is for, one pane over.
  // Focus mode hides the navigator regardless of framework count (design
  // spec "Chrome"): one Verordnung's mask/document at a time is the point.
  const showNavigator = frameworks.length >= 2 && !showSupplierGate && !focus
  // LOADING IS PER PANE, NOT PAGE-WIDE. The review reads (`state.isLoading`:
  // statements, read-model, article-states, condition-fields, scope) feed the
  // navigator, the mask and the footer only; the document pane needs nothing
  // but the id, so it mounts at once and its byte fetch runs alongside them
  // instead of after the slowest one. The navigator's slot is held open while
  // loading (the usual multi-Verordnung shape), so the panes do not all jump
  // sideways the moment the reads land; a single-Verordnung declaration then
  // settles into its 2-pane layout.
  const loading = state.isLoading
  // Paged (C3-b2): the navigator (server summary) and the mask (the open
  // regulation's slice) leave their skeletons separately. Absent: both follow
  // `loading`, as today.
  const navigatorLoading = state.navigatorLoading ?? loading
  const maskLoading = state.maskLoading ?? loading
  // Paged: a navigator whose summary failed after the page was on screen keeps its
  // slot and shows an error with a retry, instead of collapsing (the reviewer could
  // otherwise not switch regulation at all) or holding a skeleton forever.
  const navigatorError = state.navigatorError === true
  const navigatorSlot =
    navigatorLoading || navigatorError
      ? !showSupplierGate && !focus
      : showNavigator
  const activeIndex = activeFramework ? frameworks.indexOf(activeFramework) : -1
  const activeDerivation = activeFramework
    ? (state.derivations.get(activeFramework) ?? null)
    : null
  const activeOpenIssues = activeFramework
    ? (state.openByFramework.get(activeFramework) ?? [])
    : []
  // WorklistIssue.articleIds is actually article NUMBERS (cellState.ts's own
  // naming gap), not the real mc_articles.id the inquiry-create endpoint
  // needs — resolved here off `state.pool` (get_scope's article_pool/
  // candidate_articles, already fetched for this declaration, now carrying
  // `id` alongside `articleNumber`) rather than a fresh fetch. A number with
  // no pool entry (a statement-only reference never linked to master data)
  // is dropped, not sent — no worse than the create endpoint's own silent-
  // drop on an unresolvable id.
  const articleIdByNumber = new Map(
    state.pool.map((a) => [a.articleNumber, a.id]),
  )
  const activeOpenArticleIds = [
    ...new Set(
      activeOpenIssues
        .flatMap((i) => i.articleIds)
        .map((number) => articleIdByNumber.get(number))
        .filter((id): id is string => !!id),
    ),
  ]
  const nameOf = (fw: string) =>
    frameworkDisplayName(fw, state.regulationNameByFramework, translate)
  // Paged (C3-b2): the pool with ids may not be loaded yet when Anfordern is pressed,
  // so the open regulation's article numbers are resolved at click time against
  // `/scope`, fetched on demand.
  const resolvePagedOpenArticleIds = async (): Promise<string[]> => {
    if (!queryClient) return []
    const scope = await loadDeclarationScope(queryClient, state.declId)
    const pool = scope?.articlePool?.length
      ? scope.articlePool
      : (scope?.candidateArticles ?? [])
    const idOf = new Map(pool.map((a) => [a.articleNumber, a.id]))
    return [
      ...new Set(
        activeOpenIssues
          .flatMap((i) => i.articleIds)
          .map((number) => idOf.get(number))
          .filter((id): id is string => !!id),
      ),
    ]
  }
  // Paged (C3-b2): the approved dialog's matrix needs every regulation, loaded once
  // the approval landed (`loadPagedApprovedSnapshot`); null — no matrix yet — while
  // it loads or if it failed (the dialog itself never waits on it).
  const [pagedSnapshot, setPagedSnapshot] = useState<ApprovedSnapshot | null>(
    null,
  )
  // No "already loaded for this result" ref guard: under StrictMode the effect runs,
  // is cleaned up (`live = false`) and runs again, and a guard would drop the second
  // run's result. The deps already run it once per approval in production.
  useEffect(() => {
    if (!state.paged || !approveResult || !queryClient) return
    let live = true
    loadPagedApprovedSnapshot({
      queryClient,
      declId: state.declId,
      frameworks,
      nameOf,
    }).then(
      (snapshot) => {
        if (live) setPagedSnapshot(snapshot)
      },
      () => {
        if (live) setPagedSnapshot(null)
      },
    )
    return () => {
      live = false
    }
    // `frameworks`/`nameOf` as of the approval; the effect runs once per result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.paged, approveResult, queryClient, state.declId])
  // Every statement card, every Verordnung (TUR-1211): the request dialog's
  // draft-endpoint fallback body (`RequestQuestionsDialog`'s own `cards`
  // prop) uses it to open a collected question's block with its statement's
  // OWN `draftFor` paragraph. A plain `flatMap`, not a memo: the dialog only
  // reads this once, on open (its own `runDraft` effect is keyed `[open]`),
  // so a fresh array identity on an unrelated re-render costs nothing.
  // Paged (C3-b3): the open regulation's cards plus the others' as last loaded
  // (`usePagedGestures`); the dialog and the map load the rest before they read.
  const activeCardIds = new Set(
    (state.statementCardsByFramework.get(activeFramework ?? "") ?? []).map(
      (c) => c.id,
    ),
  )
  const allCards = everyCardOnHand(
    frameworks,
    activeFramework,
    state.statementCardsByFramework,
    pagedGestures?.otherCards,
  )

  // WHAT ANFORDERN TELLS THE COMPOSER (TUR-1211, Erik 02.09 bug 3). #2120 handed
  // it the supplier and the open article ids; the composer still drafted a
  // four-framework questionnaire with no word about WHICH declaration or gap
  // it was for. This names the Verordnung (composer preselects one framework),
  // the declaration and the statement (persisted as `source_*`, like the
  // statement-level Rückfrage), and one translated line the composer appends
  // to the mail under "Bezug:". The statement is the URL-focused one when it
  // belongs to the active framework, else the first one with open step-3
  // work; the missing fields come from the published M6 validation (verdict
  // doubt only — a data gap is not a blocker and not what the reviewer is
  // stuck on). Labels reuse the condition spec's own field labels.
  const requestContext: RequestContext | null = activeFramework
    ? (() => {
        const openStep3Ids = new Set(
          activeOpenIssues
            .filter((i) => i.unit === "statement" && i.step === 3)
            .map((i) => i.statementId ?? i.unitId),
        )
        const focused = state.url.state.statementId ?? null
        const statementId =
          focused && activeCards.some((c) => c.id === focused)
            ? focused
            : (activeCards.find((c) => openStep3Ids.has(c.id))?.id ?? null)
        const statementIndex = activeCards.findIndex(
          (c) => c.id === statementId,
        )
        const spec = conditionSpec(activeFramework)
        const missingLabels = [
          ...new Set(
            (state.readModel?.condition_fill?.validation ?? [])
              .filter((f) => f.framework === activeFramework && !f.data_gap)
              .flatMap((f) => f.missing)
              .filter((m) => !m.startsWith("<")),
          ),
        ].map((key) => {
          if (key === "concentration" && spec.concentration)
            return translate(spec.concentration.label)
          const field = spec.fields.find((fld) => fld.key === key)
          return field ? translate(field.label) : key
        })
        const line = [
          activeDeclaration?.declarationNumber ?? null,
          nameOf(activeFramework),
          statementIndex >= 0
            ? translate("MC_DECL_V2_UNIT_LABEL", { i: statementIndex + 1 })
            : null,
          missingLabels.length > 0
            ? translate("MC_DECL_V2_REQUEST_CONTEXT_MISSING", {
                fields: missingLabels.join(", "),
              })
            : null,
        ]
          .filter((part): part is string => !!part)
          .join(" · ")
        return {
          declarationId: state.declId,
          declarationNumber: activeDeclaration?.declarationNumber ?? null,
          framework: activeFramework,
          statementId,
          line: line || null,
        }
      })()
    : null
  const nextDeclarationId = nextOpenDeclaration(declarations, state.declId)
  // `activeDeclaration` — honest, non-paginated data for THIS declaration
  // only (never a new query, unlike a worklist-wide total, see
  // `DeclProgressBar`'s own doc for why that one stays unwired) — is read
  // once further up (see `isWorkbook`'s own doc) so the guard below and the
  // panel's own prop narrow off the SAME value (control-flow narrowing on an
  // optional-chained property read twice is not guaranteed, a local const is).
  const activeLifecycleStatus = activeDeclaration?.lifecycleStatus
  // B2 — single source of truth for "this declaration is a terminal,
  // already-approved static panel", shared by the keyboard-disable guard
  // below and the early-return branch further down. Computed from the same
  // `activeLifecycleStatus` const so the two never drift apart. F10 — "expired"
  // is a third post-approval reading (approved, valid_until has lapsed) and
  // belongs here too: without it a deep link into an expired declaration would
  // fall through to the editable workbench below instead of this static panel.
  const isAlreadyApproved = isPostApproval(activeLifecycleStatus)
  // B1 — every card of this framework is `excluded` (rejected): the
  // framework's own checked sentence must say so distinctly (see
  // `FrameworkRow`'s own doc). A framework with no cards at all (still
  // loading) reads as NOT all-excluded — `every()` on an empty array is
  // vacuously true, which would be wrong here.
  const allExcludedOf = (fw: string): boolean => {
    const cards = cardRefsOf(fw)
    return cards.length > 0 && cards.every((c) => c.excluded)
  }

  // Shared by both the successful-conform path and the R1-blocked path below:
  // advance to the next un-geprüft framework (wrapping past the end). Starts
  // the search AFTER the current index on purpose — the invalidation a
  // successful `conformFramework` fires is still in flight when it resolves,
  // so `state.geprueft(activeFramework)` may still read stale-open for one
  // more render; searching past it rather than at it means Enter always
  // visibly moves forward instead of re-landing where it started.
  //
  // `announceNoneLeft` GATES THE "ALL CHECKED" TOAST ON FINDING NOTHING TO
  // ADVANCE TO — true only from the successful-conform caller, where it is
  // actually true (the framework Enter just confirmed plus every other one
  // already geprüft IS "all checked"). The R1-blocked caller passes false:
  // there `active` itself is demonstrably NOT checked (that is the whole
  // reason Enter just refused it), so finding no OTHER open framework must
  // stay silent rather than announce "all regulations checked" one toast
  // after telling the reviewer the opposite.
  const advancePastActive = (active: string, announceNoneLeft: boolean) => {
    const startAt = frameworks.indexOf(active)
    let next: string | null = null
    for (let step = 1; step <= frameworks.length; step++) {
      const candidate = frameworks[(startAt + step) % frameworks.length]
      if (candidate === active) break // wrapped all the way back
      if (!state.geprueft(candidate)) {
        next = candidate
        break
      }
    }
    if (next) {
      state.url.patch({ framework: next })
    } else if (announceNoneLeft) {
      toast.success(translate("MC_DECL_V2_FOOTER_ALL_CHECKED_TOAST"))
    }
  }

  // Entering focus mode (mount with `focus` already on, or the toggle
  // turning it on) skips a Verordnung that is already geprüft, the flow's
  // step model only stops on open ones (design spec "Entry and exit"). An
  // ARMED ref, not a `[focus]`-only effect: `activeFramework` can still be
  // null (cards not loaded yet) or the supplier gate can still be up on the
  // very render focus turns on, and a `[focus]`-only effect would never
  // re-run once either resolves a beat later. This effect re-runs on every
  // one of those three deps, and the ref is what keeps it a ONE-TIME skip
  // rather than re-firing on each. Behind the supplier gate `geprueft` is
  // derived from pre-supplier statements the gate exists to keep the
  // reviewer from trusting (same reason `showNavigator` hides its checkmarks
  // there), so it stays armed and waits there too. Turning focus off re-arms
  // it, so the next time in gets its own fresh skip.
  const entrySkipArmed = useRef(true)
  useEffect(() => {
    if (!focus) {
      entrySkipArmed.current = true
      return
    }
    if (!entrySkipArmed.current) return
    // Not on half-loaded reads: `geprueft` over a context still missing its
    // scope would skip a Verordnung that is not actually checked.
    if (loading || showSupplierGate || !activeFramework) return
    // Disarm BEFORE the skip fires, not after: this is what makes the skip
    // fire AT MOST ONCE per focus session, so a reviewer who navigates back
    // to this same checked Verordnung a moment later (still in focus) is
    // never overridden a second time (review finding, PR #2318).
    entrySkipArmed.current = false
    if (state.geprueft(activeFramework)) {
      advancePastActive(activeFramework, false)
    }
    // `state.geprueft`/`advancePastActive` are deliberately not deps: both
    // are fresh closures every render (the latter closes over `frameworks`/
    // `state.url.patch` directly), and listing them would re-arm this
    // effect's disarm/skip logic on every unrelated re-render, not just on
    // an actual change of focus/gate/framework. React StrictMode's double-
    // invoke of this effect is a no-op here: the second call reads the ref
    // already disarmed by the first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, loading, showSupplierGate, activeFramework])

  // Enter on a multi-Verordnung declaration: bundle-conform the ACTIVE one,
  // then advance past it (see `advancePastActive`).
  //
  // R1 (the article scope is unresolvable, or a Rückfrage is already out —
  // `deriveFrameworkVerdict`'s own cascade; a merely drafted one does not
  // block, see `conformBlockReason`) BLOCKS THE GESTURE OUTRIGHT: the article
  // scope or the supplier's answer for this Verordnung is still open, so signing
  // off every statement underneath it would sign off work the reviewer
  // cannot actually have verified. Enter still ADVANCES past it though — the
  // no-dead-end invariant (§ this fix): a reviewer who keeps hitting Enter on
  // a multi-Verordnung declaration must be able to move on to the ones that
  // ARE ready, exactly like the bulk gesture silently skipping an ineligible
  // framework rather than stalling on it.
  const handleConformActiveAndAdvance = async () => {
    if (!activeFramework) return
    const blockReason = conformBlockReason(
      activeDerivation,
      state.inquiryArticleCountOf(activeFramework),
    )
    if (blockReason) {
      toast.error(
        translate(
          blockReason === "scope-unresolved"
            ? "MC_DECL_V2_CONFORM_BLOCKED_SCOPE"
            : "MC_DECL_V2_CONFORM_BLOCKED_INQUIRY",
          { framework: nameOf(activeFramework) },
        ),
      )
      advancePastActive(activeFramework, false)
      return
    }
    // A single-framework call: no bulk run to thread `rows` across, so the
    // returned row set is ignored — `conformFramework` falls back to the
    // live query snapshot on its own (see its own doc).
    const { ok, openScopeStatements } = await conformFramework(
      activeFramework,
      activeCards,
      activeOpenIssues,
    )
    if (!ok) return
    if (openScopeStatements > 0) {
      // The run saved what it could, but the framework is NOT geprüft yet —
      // its article scope still awaits the card's own Apply. Advancing is
      // still right (Enter's contract is "move forward"; the reviewer comes
      // back via the still-open navigator row), but announcing "all checked"
      // over it would be false, hence `false`.
      toast.warning(
        translate("MC_DECL_V2_CONFORM_PARTIAL_SCOPE", {
          n: openScopeStatements,
        }),
      )
      advancePastActive(activeFramework, false)
      return
    }
    toast.success(translate("MC_DECL_V2_CONFORM_SUCCESS"))
    advancePastActive(activeFramework, true)
  }

  // A "next declaration" jump carries the overview's own list filters plus
  // `focus=1` when this workspace has it (design spec "Flow": focus mode
  // survives across declarations). `nextDeclarationSearch` (studioV2Url.ts)
  // is the ONE implementation shared with `StudioV2FooterBar`'s own Skip
  // navigation, not a second copy.
  const goToNextOrDone = () => {
    if (nextDeclarationId) {
      navigate(
        `${DECLARATIONS_BASE}/${nextDeclarationId}/review${nextDeclarationSearch(searchParams)}`,
      )
    } else {
      setShowDone("completed")
    }
  }

  useStudioV2Keyboard({
    frameworks,
    activeFramework,
    onSelect: (fw) => state.url.patch({ framework: fw }),
    primaryAction: state.primaryAction,
    onApprove: () => footerRef.current?.approve(),
    onRequest: () => footerRef.current?.request(),
    onDefer: () => footerRef.current?.defer(),
    onConformActive: handleConformActiveAndAdvance,
    onToggleHelp: () => helpRef.current?.toggle(),
    onNext: handleNextOpenPoint,
    // Esc closes the mask sheet (the article browser or the substance
    // table, each a focused step over the mask) first; only with none open
    // does it leave for the list. Routed through `handleMaskSheetChange`,
    // NOT a bare `setMaskSheet(null)` (review finding, wave 5): neither sheet
    // stops this key from bubbling to this global `window` listener when the
    // press did not start inside the sheet's own DOM, so a bare
    // `setMaskSheet(null)` closed the sheet without clearing the URL's
    // `step`, and the remounted card's focus effect reopened it at once (the
    // loop `handleMaskSheetChange`'s own doc describes for Cancel, Apply and
    // Fertig).
    onEscape: () => {
      // Focus mode is the SAME chain's next rung (design spec "Entry and
      // exit"): a sheet still wins (unchanged above), then leaving focus,
      // and only with neither open does Escape reach the list below.
      if (maskSheet) handleMaskSheetChange(null)
      else if (focus) state.url.patch({ focus: false })
      else navigate(DECLARATIONS_BASE)
    },
    // This call sits ABOVE the `showDone`/`approveResult`/`isAlreadyApproved`
    // early returns below (hooks cannot be called conditionally), so without
    // this the hook stayed fully live on every terminal screen — Enter/1/2/3
    // kept firing real mutations (approve/request/defer, bundle-conform)
    // against a declaration the reviewer had already left. B2's static panel
    // is a third such screen: `footerRef`/`helpRef` are null there so 1/2/3
    // are harmless by accident, but `onConformActive` is a direct closure,
    // not a ref, so Enter would still fire `conformFramework` for real.
    // Nor while the review reads are still loading: the footer those keys
    // drive is a skeleton then, and a primaryAction derived from an empty
    // worklist is not one the reviewer has seen.
    enabled: !loading && !showDone && !approveResult && !isAlreadyApproved,
  })

  if (showDone) {
    return (
      <StudioV2DoneScreen
        reason={showDone}
        onBackToList={() => navigate(DECLARATIONS_BASE)}
      />
    )
  }

  // ONCE APPROVED, THE WORKBENCH STAYS AND A SELF-DISMISSING DIALOG SITS ON
  // TOP (product decision, 28.08.): no full-page swap, no mandatory click.
  // The reviewer sees the finished declaration behind the result; the
  // countdown (or Esc / "Zur Übersicht") returns to the list. The modal
  // blocks pointer input and `useStudioV2Keyboard` is already disabled on
  // `approveResult`, so the mask underneath is inert, not editable.
  // `approveResult` also wins over `isAlreadyApproved` below: a list poll
  // flipping this declaration to accepted mid-countdown must not swap the
  // background for the static panel under the reviewer.
  const approvedDialog = approveResult ? (
    <DeclarationApprovedDialog
      result={approveResult}
      supplierName={supplierName}
      frameworksLabel={frameworks.map(nameOf).join(", ")}
      // The read-model queries are disabled once `approveResult` is set, so
      // this is the state exactly as approved.
      snapshot={
        state.paged
          ? pagedSnapshot
          : buildApprovedSnapshot({
              frameworks,
              nameOf,
              cardsByFramework: state.statementCardsByFramework,
              pool: state.pool,
            })
      }
      onDone={() => navigate(DECLARATIONS_BASE)}
      // H2 (tester feedback), the SAME next `ready` declaration Skip/
      // `goToNextOrDone` already jump to (`nextDeclarationId`, computed
      // above), so "Nächste Erklärung" only renders once one is actually
      // loaded and, when clicked, lands on the exact same route the footer
      // jump does.
      nextDeclarationId={nextDeclarationId}
      onNext={() =>
        navigate(
          `${DECLARATIONS_BASE}/${nextDeclarationId}/review${nextDeclarationSearch(searchParams)}`,
        )
      }
      // The ✕ (and Esc / the backdrop) CLOSE THE RESULT, nothing more
      // (product-owner feedback, 03.09.: it used to do exactly what "Zur
      // Übersicht" does). `approveResult` deliberately STAYS set: it is what
      // keeps the keyboard shortcuts off and keeps a list poll from swapping
      // the body for `AlreadyApprovedPanel` under the reviewer — they wanted
      // to keep looking at THIS declaration, not at a terminal panel. The
      // mask is frozen alongside it (see `sv2-pane-rv`'s own class below):
      // with the modal gone there is nothing else stopping an edit on a
      // declaration that is already signed off.
      onClose={() => {}}
    />
  ) : null

  // B2 — a deep link into an already-accepted/expiring declaration must not
  // render the editable workbench: `ApprovedView` above only covers the
  // IN-SESSION approve response (it needs the POST payload, unavailable
  // after the fact), so this is a separate, static, lifecycleStatus-driven
  // panel. `activeDeclaration` comes from the SAME `declarations` list the
  // entry page's not-found guard already proved membership in on mount; if a
  // later poll drops the row (`activeDeclaration` undefined), this falls
  // through to the ordinary workbench below rather than guessing — graceful,
  // never a false "already approved" claim.
  if (isAlreadyApproved && !approveResult) {
    return (
      <AlreadyApprovedPanel
        lifecycleStatus={activeLifecycleStatus}
        onBackToList={() => navigate(DECLARATIONS_BASE)}
        openSiblingCount={state.readModel?.open_sibling_count}
        supplierId={state.supplierId}
      />
    )
  }

  const workbench = (
    <>
      {focus && (
        // Modal overlay (design spec "Chrome"): the scrim sits BEHIND the
        // panel, as a sibling rather than a wrapper, so it never becomes the
        // containing block for the panel's own `position: fixed` box.
        // Clicking it leaves focus mode: layout only, nothing is lost, all
        // review state lives in the URL and server.
        <div
          className="sv2-focus-scrim"
          data-testid="sv2-focus-scrim"
          aria-hidden="true"
          onClick={() => state.url.patch({ focus: false })}
        />
      )}
      <div
        className={cn(
          "sv2-root flex h-full min-h-0 flex-col bg-card",
          focus && "sv2-focus",
        )}
      >
        {approvedDialog}
        <div className="sv2-revtop">
          {focus ? (
            // Chrome (design spec): crumbs and the supplier/hold chips give way
            // to the plain file name, the one thing worth keeping legible once
            // the surrounding app is hidden.
            <div className="sv2-focus-file" title={activeDeclaration?.doc}>
              {activeDeclaration?.doc}
            </div>
          ) : (
            <div className="sv2-crumbs">
              <button
                type="button"
                onClick={() => navigate(DECLARATIONS_BASE)}
                className="sv2-crumb sv2-link"
              >
                {translate("MC_DECL_V2_BREADCRUMB_ROOT")}
              </button>
              <span aria-hidden="true" className="sv2-csep">
                ›
              </span>
              <span className="sv2-crumb sv2-cur">{supplierName}</span>
              {/* TUR-1452 / W3-2: this document is a returned copy of the org's
                OWN supplier-inquiry package come back unsigned, or a customer
                form come back unfilled: either way the pipeline held it
                (processed, nothing staged), which is exactly why the review
                screen below has nothing to show. `holdReason` is the SAME
                derived reason the mask body branches on below. */}
              {holdReason && (
                <RoundtripHoldPill reason={holdReason} className="ml-1" />
              )}
              {/* MC_DECL_V2_SUP: the pipeline detected an issuer master data does
                not know yet. `=== false` (never a falsy check): `null` means
                "not yet known" (extraction still loading), and the chip must
                stay quiet then rather than flash on for a beat. */}
              {state.supplierMatched === false && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <SupplierAssignControl
                      declId={state.declId}
                      supplierMentioned={state.supplierMentioned}
                      supplierName={supplierName}
                      onAssigned={handleAssigned}
                      trigger={
                        <TooltipTrigger asChild>
                          <button type="button" className="sv2-crumbchip">
                            <span className="sv2-d" />
                            {translate("MC_DECL_V2_SUP_UNMATCHED_CHIP")}
                          </button>
                        </TooltipTrigger>
                      }
                    />
                    <TooltipContent side="bottom">
                      {translate("MC_DECL_V2_SUP_UNMATCHED_TITLE")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {/* MC_SUPPLIER_REASSIGN: a supplier IS bound but the automatic
                match may have picked the wrong one. Asks before it re-reads the
                declaration against the new supplier's articles. */}
              {canReassignSupplier && (
                <SupplierReassignControl
                  declId={state.declId}
                  currentSupplierId={state.supplierId}
                  currentSupplierName={supplierName}
                  supplierMentioned={state.supplierMentioned}
                  // No `supplierName` prefill here: it is the bound supplier's
                  // own name, so creating from it would duplicate the supplier
                  // being replaced.
                  onAssigned={handleAssigned}
                  trigger={
                    <button type="button" className="sv2-crumbedit">
                      {translate("MC_DECL_V2_SUP_CHANGE")}
                    </button>
                  }
                />
              )}
            </div>
          )}
          {/* Reads its own stats (`useDeclarationStats`) — no props to thread;
            see its own doc for why it counts the whole inbox, not a session. */}
          <DeclProgressBar />
          <HelpPopover ref={helpRef} showNextShortcut />
          {/* The Fokus toggle (design spec "Entry and exit"), next to the help
            bulb, same icon-button look (`sv2-helpbtn`). Patches the URL only;
            every other consequence (chrome, the entering-focus advance
            effect above, `showNavigator`/`autoSaveId`) reads `focus` back off
            it. */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={translate(
                    focus ? "MC_DECL_V2_FOCUS_LEAVE" : "MC_DECL_V2_FOCUS_ENTER",
                  )}
                  onClick={() => state.url.patch({ focus: !focus })}
                  className={cn("sv2-helpbtn", focus && "sv2-on")}
                >
                  {focus ? (
                    <Minimize2 size={15} className="sv2-ic" />
                  ) : (
                    <Maximize2 size={15} className="sv2-ic" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {translate(
                  focus ? "MC_DECL_V2_FOCUS_LEAVE" : "MC_DECL_V2_FOCUS_ENTER",
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {/* B-back: the top bar's own "leave the workspace" affordance,
            rightmost of the progress/help cluster. The breadcrumb root link
            further left (`sv2-crumb sv2-link`, "Supplier Declarations" -
            `MC_DECL_V2_BREADCRUMB_ROOT`) already navigates to the same place,
            but reads as a text breadcrumb, not a discoverable action: this
            is the visible ghost-button twin, mirroring `Esc`
            (`useStudioV2Keyboard`'s own `onEscape`), which already lands here. */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(DECLARATIONS_BASE)}
                  className="h-[28px] gap-1.5 text-[12.5px] text-grey5 hover:text-white"
                >
                  <ArrowLeft size={13} aria-hidden="true" />
                  {translate("MC_DECL_V2_BACK_TO_LIST")}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {translate("MC_DECL_V2_BACK_TO_LIST_HINT")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {/* Task 6 — the open-sibling hint (decision Ferdi 2026-09-04: option 2, no
          automatic supersede). A full-width line under the topbar rather than a
          breadcrumb chip: the sentence copy needs more room than `.sv2-crumbchip`'s
          nowrap pill has, and the fixed-height crumb row is already full. Same
          amber the unmatched-supplier chip and `.sv2-warn` use for a warn state.
          `state.supplierId` is the SAME resolved id `GET /extraction` returns as
          `supplier` (v1's `MissionControlBoard` reads it off that query directly);
          it is unset in exactly the case `open_sibling_count` is always 0. F9 —
          `AlreadyApprovedPanel` renders the SAME `OpenSiblingHint` INSIDE its own
          `.sv2-donecard` (see below, `variant="card"`), so an already-approved
          declaration shows this too — bug fix 2026-09-08: it used to sit as its
          own full-width bar above the card, reading as a stray, uninviting strip
          detached from the centered card it was supposed to point away from.
          H1 (tester feedback, "necessary?"), the topbar placement now takes the
          SAME quiet card look the already-approved panel uses below: calm
          yellow, underlined link, no full-width strip. `variant="inline"`
          keeps that look but drops the card's 24px bottom margin, which was
          sized for the done-card and left a blank gap under this topbar. */}
        {/* Hidden in focus mode (design spec "Chrome"), the same reasoning as
          the crumbs it sits directly under. */}
        {!focus && (
          <OpenSiblingHint
            supplierId={state.supplierId}
            count={state.readModel?.open_sibling_count}
            variant="inline"
          />
        )}
        {/* Asked against delivered for the inquiry this declaration answers
          (TUR-1732), with a Rückfrage for what is missing. Same quiet card. */}
        {!focus && (
          <InquiryCoverageHint
            declarationId={state.declId}
            declarationNumber={activeDeclaration?.declarationNumber ?? null}
            supplierId={state.supplierId}
          />
        )}
        {/* `autoSaveId` differs by panel count/order (see the module doc) — a
          2-pane, a 3-pane and a focus-mode declaration never share a saved
          layout (design spec "Chrome"). Focus is checked first: it always
          implies `!showNavigator`, but names its OWN id rather than falling
          into "sv2-panels-2" so a focus-mode drag never collides with an
          ordinary single-Verordnung declaration's saved sizes. */}
        <ResizablePanelGroup
          orientation="horizontal"
          autoSaveId={
            focus
              ? "sv2-panels-focus:v4"
              : navigatorSlot
                ? "sv2-panels-3:v4"
                : "sv2-panels-2:v4"
          }
          className="sv2-rev3"
        >
          {navigatorSlot && (
            <>
              {/* Product-owner sizing (25.08., screenshot-calibrated): a slim
                navigator so the document pane gets half the screen — the
                prototype's 18% navigator spent width the reviewer wants on
                the document. */}
              <ResizablePanel
                id="navigator"
                defaultSize={12}
                minSize={10}
                maxSize={30}
              >
                {navigatorError ? (
                  <PaneError
                    testId="sv2-navigator-error"
                    onRetry={state.retryNavigator}
                  />
                ) : navigatorLoading ? (
                  <div
                    className="sv2-pane p-3"
                    data-testid="sv2-navigator-skeleton"
                    {...(state.paged && {
                      role: "status",
                      "aria-busy": true,
                      "aria-label": translate("MC_DECL_V2_LOADING"),
                    })}
                  >
                    <Skeleton className="h-full w-full rounded-lg" />
                  </div>
                ) : (
                  <FrameworkNavigator
                    frameworks={frameworks}
                    nameOf={nameOf}
                    derivationOf={(fw) => state.derivations.get(fw) ?? null}
                    geprueft={state.geprueft}
                    activeFramework={activeFramework}
                    onSelect={(fw) => state.url.patch({ framework: fw })}
                    inquiryStateOf={state.inquiryStateOf}
                    allExcludedOf={allExcludedOf}
                    regulationStatusOf={state.regulationStatusOf}
                  />
                )}
              </ResizablePanel>
              <ResizableHandle className="sv2-handle" />
            </>
          )}
          {/* Product-owner sizing (25.08.): mask ~38%, document gets the rest
            (half the screen with the navigator up). `minSize` is generous
            enough for the prototype's 392px floor at common widths. */}
          <ResizablePanel
            id="mask"
            defaultSize={navigatorSlot ? 38 : 42}
            minSize={28}
          >
            <div
              className={cn(
                "sv2-pane sv2-pane-rv",
                // Approved: no more editable work here. While the result dialog
                // is up the modal already blocked every pointer event; once it
                // is closed by the ✕ this is what keeps the mask inert. The
                // DOCUMENT pane is deliberately left alone — reading, scrolling
                // and zooming the source is the whole reason to stay.
                approveResult && "pointer-events-none opacity-80",
              )}
              // `pointer-events-none` above only blocks clicks — a control that is
              // already focused (or reached by Tab after the ✕ closes the dialog)
              // can still take keyboard input, and SubstanceEditor commits its
              // text field on blur regardless of approval state. The real `inert`
              // attribute is what removes the whole subtree from focus and input,
              // not just pointer events. React 19 treats `inert` as a boolean prop:
              // `true` renders `inert=""`, `false` omits the attribute entirely.
              inert={Boolean(approveResult)}
            >
              {showSupplierGate ? (
                <SupplierGate
                  declId={state.declId}
                  supplierMentioned={state.supplierMentioned}
                  supplierName={supplierName}
                  recomputing={recomputing}
                  onAssigned={handleAssigned}
                  memoryHint={memoryEnabled ? state.memorySupplier : null}
                  nameSuggestions={
                    supplierMatchStrict ? state.supplierSuggestions : undefined
                  }
                />
              ) : holdReason ? (
                <ExtractionHoldPanel
                  declarationId={state.declId}
                  reason={holdReason}
                  onRequest={() => footerRef.current?.request()}
                />
              ) : state.maskError ? (
                <PaneError testId="sv2-mask-error" onRetry={state.retryMask} />
              ) : maskLoading ? (
                <div
                  className="flex h-full flex-col gap-3 p-3"
                  data-testid="sv2-mask-skeleton"
                  {...(state.paged && {
                    role: "status",
                    "aria-busy": true,
                    "aria-label": translate("MC_DECL_V2_LOADING"),
                  })}
                >
                  <Skeleton className="h-10 w-full rounded-lg" />
                  <Skeleton className="h-24 w-full rounded-lg" />
                  <Skeleton className="h-24 w-full rounded-lg" />
                </div>
              ) : activeFramework ? (
                <>
                  {state.maskRefreshError && (
                    <PaneRefreshError onRetry={state.retryMask} />
                  )}
                  <ReviewMaskPane
                    declId={state.declId}
                    framework={activeFramework}
                    frameworkIndex={activeIndex}
                    frameworkCount={frameworks.length}
                    frameworkName={nameOf(activeFramework)}
                    derivation={activeDerivation}
                    inquiryArticleCount={state.inquiryArticleCountOf(
                      activeFramework,
                    )}
                    draftedQuestionCount={state.draftedQuestionCountOf(
                      activeFramework,
                    )}
                    cards={activeCards}
                    openIssues={activeOpenIssues}
                    pool={state.pool}
                    poolLoading={state.poolLoading}
                    poolError={state.poolError}
                    onRetryPool={state.retryPool}
                    issuerProposal={state.issuerProposal}
                    uncoveredClaimsByArticle={state.uncoveredClaimsByArticle}
                    readerHintsByArticle={state.readerHintsByFramework.get(
                      activeFramework,
                    )}
                    onJumpToSheetRow={
                      isWorkbook ? handleJumpToSheetRow : undefined
                    }
                    isWorkbook={isWorkbook}
                    onJumpToEvidence={handleJumpToEvidence}
                    editionInfo={state.editionInfo.get(activeFramework) ?? null}
                    focusedStatementId={state.url.state.statementId}
                    focusedStep={state.url.state.step}
                    frameworks={frameworks}
                    nameOf={nameOf}
                    // Land the mask on the freshly created card — the same URL
                    // shape every gate/map jump uses.
                    onStatementCreated={(framework, statementId) =>
                      state.url.patch({ framework, statementId, at: "mask" })
                    }
                    externalPick={externalPick}
                    onAddDialogOpenChange={setAddDialogOpen}
                    // "Verordnung als geprüft markieren" ends where Enter ends:
                    // on the next un-geprüfte Verordnung (`advancePastActive`,
                    // announcing "all checked" only when there is none left).
                    // The pane fires this ONLY on a run that left no open work
                    // behind — a partial run keeps its own in-pane jump.
                    onFrameworkChecked={(fw) => advancePastActive(fw, true)}
                    focusNonce={focusNonce}
                    maskSheet={maskSheet}
                    onMaskSheetChange={handleMaskSheetChange}
                    findTokens={findTokens}
                    findSearch={findSearch}
                    onClearFind={handleClearFind}
                    onFindRequest={requestFindFocus}
                  />
                </>
              ) : awaitingExtraction ||
                activeLifecycleStatus === "pending" ||
                activeLifecycleStatus === "processing" ? (
                // B5 — the extraction has not finished: `frameworks` is empty
                // because nothing has been read yet, not because the document
                // truly carries no regulation. Saying so as a final fact (the
                // branch below) would be a lie with an end date already known.
                // TUR-1211 W2-4: this run can take up to two hours before the
                // backend marks it failed, so `ExtractionProgress` is mounted
                // here (and only here) to arm `useRunPoll` and show the live
                // node feed + elapsed time instead of a bare spinner.
                //
                // `activeLifecycleStatus` is checked ALONGSIDE the caller's own
                // `awaitingExtraction` prop, not instead of it (TUR-1211 wave
                // 3): the prop is threaded from a SEPARATE read
                // (`DeclarationReviewPage`'s own `declaration`), so a caller
                // whose `declarations` list already carries a pending row can
                // still land here with a stale/omitted prop. Falling through
                // to the "no regulation" div below for a declaration that is
                // still being read would be a lie with an end date already
                // known, same as the comment above says of `awaitingExtraction`
                // alone.
                <ExtractionProgress
                  declId={state.declId}
                  since={activeDeclaration?.capturedAt ?? null}
                  largeScopeArticleCount={
                    activeDeclaration?.largeScope
                      ? (activeDeclaration.scopeArticleCount ?? null)
                      : null
                  }
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-card text-[12px] text-grey5">
                  {translate("MC_DECL_V2_NO_FRAMEWORKS")}
                </div>
              )}
            </div>
          </ResizablePanel>
          <ResizableHandle className="sv2-handle" />
          {/* The rest of the row: 45% (3-pane) / 65% (2-pane). */}
          <ResizablePanel
            id="document"
            defaultSize={navigatorSlot ? 45 : 65}
            minSize={navigatorSlot ? 20 : 30}
          >
            <DocumentPane
              declId={state.declId}
              isWorkbook={isWorkbook}
              zoom={state.url.state.zoom}
              onZoomChange={(zoom) => state.url.patch({ zoom })}
              jumpToPage={jumpToPage}
              // L8: the single owner now (this file's own `barOpen` state):
              // `DocumentPane` no longer keeps its own copy, so its own
              // remount (a framework switch, a mask-sheet toggle) can no
              // longer silently reopen or reset what the reviewer closed.
              barOpen={barOpen}
              onBarOpenChange={handleBarOpenChange}
              evidenceFlash={evidenceFlash}
              workbookSources={workbookSources}
              // The STATE object itself, not a re-wrapped `{ key: … }` literal:
              // `DocumentPane`/`DeclarationWorkbookViewer` both key a `useEffect`
              // off this prop's IDENTITY (`[workbookFocus]`/`[focusRequest]`),
              // treating any new reference as a fresh click — a re-wrap here
              // would hand them a new object on every render (including a bare
              // refetch), re-scrolling to a stale proof each time. `workbookFocus`
              // (the state above) already carries `{key, statementId}`, a
              // structural superset of the `{key: string}` prop shape, and only
              // changes reference when `setWorkbookFocus` actually runs.
              workbookFocus={workbookFocus}
              workbookRowFocus={workbookRowFocus}
              onWorkbookRowFocusEnd={handleWorkbookRowFocusEnd}
              activeStatementId={workbookFocus?.statementId ?? null}
              fileName={activeDeclaration?.doc}
              evidencePickLabel={
                isWorkbook
                  ? undefined
                  : translate(
                      addDialogOpen
                        ? "MC_DECL_V2_SELECTION_ATTACH"
                        : "MC_DECL_V2_SELECTION_ADD",
                    )
              }
              onEvidencePick={isWorkbook ? undefined : handleEvidencePick}
              markMode={!isWorkbook && markMode}
              // TUR-1211 (W5-1): the toggle itself now lives in the document
              // pane's own toolbar (`DocumentPane`'s `.sv2-pnh`), not floated
              // over the app's global top bar: it stayed the layout's own
              // state/setter, DocumentPane only renders the control and calls
              // this back. The workbook guard (no toggle, no PDF page to mark
              // boxes on) moved with it: `DocumentPane` only renders the
              // button in its non-workbook branch.
              onToggleMarkMode={() => setMarkMode((m) => !m)}
              marks={marks}
              onMark={handleMark}
              findTokens={findTokens}
              findSearch={findSearch}
              onFindTokensChange={setFindTokens}
              onFindSearchChange={setFindSearch}
              findFocusNonce={findFocusNonce}
              // The browser kinds only: the substance sheet does not consume
              // the find-bar expression, so its placeholder must not claim it
              // does either. L8: opening ANY mask sheet, this one included,
              // no longer pops the bar open by itself (`DocumentPane` reads
              // this prop for its placeholder text alone now).
              articleBrowserOpen={
                maskSheet !== null && maskSheet.kind !== "substances"
              }
              // ANY kind (task 3, review finding wave 5): disables the mark
              // toggle regardless of which sheet is open, unlike
              // `articleBrowserOpen` above.
              sheetOpen={maskSheet !== null}
            />
            {pendingMark && (
              <MarkQuestionPopover
                anchor={pendingMark.anchor}
                quote={pendingMark.mark.quote}
                targets={markTargets}
                framework={pendingMark.framework}
                statementId={pendingMark.statementId}
                onTargetChange={(framework, statementId) =>
                  setPendingMark((prev) =>
                    prev ? { ...prev, framework, statementId } : prev,
                  )
                }
                question={pendingMark.question}
                onQuestionChange={(question) =>
                  setPendingMark((prev) =>
                    prev ? { ...prev, question } : prev,
                  )
                }
                onCancel={() => setPendingMark(null)}
                onConfirm={confirmPendingMark}
                confirmDisabled={createQuestion.isPending}
              />
            )}
          </ResizablePanel>
        </ResizablePanelGroup>
        {/* NO standalone keyboard-hint strip between the panes and the footer
          bar: it was briefly mounted here (`.sv2-kbhint`), and the product
          owner cut it — a full-width text band in that seam read as clutter,
          not guidance. The shortcut scheme stays discoverable where it
          already lives: the help popover ("?" or the bulb). */}
        {loading ? (
          <div
            className="sv2-revfoot"
            data-testid="sv2-footer-skeleton"
            {...(state.paged && {
              role: "status",
              "aria-busy": true,
              "aria-label": translate("MC_DECL_V2_LOADING"),
            })}
          >
            <Skeleton className="ml-auto h-7 w-64" />
          </div>
        ) : (
          <StudioV2FooterBar
            ref={footerRef}
            declId={state.declId}
            frameworks={frameworks}
            worklist={state.worklist}
            openItemsStatus={state.openItemsStatus}
            primaryAction={state.primaryAction}
            nameOf={nameOf}
            nextDeclarationId={nextDeclarationId}
            onApproved={setApproveResult}
            // The 409 ALREADY_APPROVED race (another reviewer got there first):
            // no `ApproveResult` payload exists to hand `ApprovedView`, so this
            // reuses the SAME "move on" path a genuine local approval finishes
            // on rather than inventing a third done-screen reason — from this
            // reviewer's own perspective there is no more open work left on the
            // declaration either way (next `ready` declaration if one is
            // loaded, else the done screen's "completed" framing). Without this
            // wire the reviewer stayed parked in an editable mask on an already-
            // approved declaration, one stray edit away from a silent
            // post-approval write (server-side pre-existing defect, out of
            // scope here).
            onAlreadyApproved={goToNextOrDone}
            inquiryStateOf={state.inquiryStateOf}
            inquiryArticleCountOf={state.inquiryArticleCountOf}
            inquiryIsDeadAddressOf={state.inquiryIsDeadAddressOf}
            onNoNextDeclaration={() => setShowDone("no-more-loaded")}
            supplierMatched={state.supplierMatched}
            supplierId={state.supplierId}
            supplierMentioned={state.supplierMentioned}
            onReextract={canReextract ? handleAssigned : undefined}
            openArticleIds={activeOpenArticleIds}
            resolveOpenArticleIds={
              state.paged ? resolvePagedOpenArticleIds : undefined
            }
            requestContext={requestContext}
            cards={allCards}
            loadAllCards={pagedGestures?.loadOtherCards}
            // The summary's verdict wins over a KEPT card of another regulation;
            // the open regulation's cards are live and answer for themselves.
            summaryVerdictOf={
              state.summaryVerdictOf
                ? (statementId: string) =>
                    activeCardIds.has(statementId)
                      ? undefined
                      : state.summaryVerdictOf?.(statementId)
                : undefined
            }
            onNextOpenPoint={handleNextOpenPoint}
            // Routed through `goToOpenPoint` so the focus nonce bumps: a jump to
            // the ALREADY-focused statement re-runs the mask's focus effect
            // instead of silently no-opping (the map shared that latent bug).
            // `step` (W5-4) rides along unchanged: `goToOpenPoint` is what
            // turns it into `&step=` and clears it on a step-less jump.
            onGoToStatement={(framework, statementId, step) =>
              goToOpenPoint({ kind: "statement", framework, statementId, step })
            }
            labelOf={labelOf}
            cardOrderOf={cardOrderOf}
            // D1's map: blockers without a statement to land on (reference-list
            // conflict, open Rückfrage) jump to the Verordnung itself.
            onGoToFramework={(framework) =>
              goToOpenPoint({
                kind: "framework",
                framework,
                unitId: frameworkUnitId(framework),
              })
            }
            // The declaration-wide bulk gesture lives in the action bar's left
            // slot (not the navigator's footer — see StudioV2FooterBar's prop
            // doc). Only offered alongside a navigator: a single-Verordnung
            // declaration has nothing to bulk over.
            bulkAction={
              <>
                <LinkAllUnresolvedSubstances
                  declId={state.declId}
                  cards={allCards}
                  // Paged: the click loads every regulation (fresh) first.
                  loadAllCards={pagedGestures?.loadEveryCard}
                />
                {showNavigator && (
                  <BulkConformGesture
                    declId={state.declId}
                    frameworks={frameworks}
                    derivationOf={(fw) => state.derivations.get(fw) ?? null}
                    // Flag off: the browser's own issues, with their article lists
                    // (bulk conform signs articles). Paged: which regulations have
                    // open work at all (the server's counts); each one acted on is
                    // loaded first and its OWN issues replace these (`loadState`).
                    openByFramework={state.openByFramework}
                    statementCardsByFramework={state.statementCardsByFramework}
                    nameOf={nameOf}
                    // Held back from the one-click sweep: a softened condition
                    // finding is a contradiction the reviewer has to look at, not
                    // a routine sign-off (see `eligible` in BulkConformGesture).
                    // Paged: each loaded regulation's own softened cells instead.
                    softenedCells={state.ctx?.conditionFindingUnconfirmed}
                    loadState={pagedGestures?.loadState}
                    onRunSettled={pagedGestures?.forgetOtherCards}
                  />
                )}
              </>
            }
            // Focus mode's compact bar (design spec "Flow"): the same bundle-
            // conform-then-advance gesture Enter fires, and a framework-only
            // skip that never touches the whole-declaration Skip/navigate path.
            // Skip does not CONFORM the active Verordnung, only advances past
            // it: `announceNoneLeft` must be false, the same as the R1-blocked
            // path above, or skipping the last open Verordnung would toast
            // "all checked" while it is still unchecked. Undefined outside
            // focus mode, not a `compact: false` object, so the footer's own
            // `compact = focusFooter !== undefined` derivation reads it right.
            focusFooter={
              focus
                ? {
                    onConformActive: handleConformActiveAndAdvance,
                    onSkipFramework: () =>
                      activeFramework &&
                      advancePastActive(activeFramework, false),
                    activeFrameworkGeprueft: activeFramework
                      ? state.geprueft(activeFramework)
                      : true,
                  }
                : undefined
            }
          />
        )}
      </div>
    </>
  )
  return workbench
}

/** Paged: the open regulation's refetch failed while its older data stays on
 *  screen. A one-line notice with a retry; the server's open items stand for the
 *  regulation meanwhile, so nothing turns green from the older data. */
function PaneRefreshError({ onRetry }: { onRetry?: () => void }) {
  const { translate } = useTranslation()
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-grey5"
      data-testid="sv2-mask-refresh-error"
      role="alert"
    >
      <span>{translate("MC_DECL_V2_PANE_REFRESH_ERROR")}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="sv2-btn sv2-btn-sm">
          {translate("MC_DECL_BTN_RETRY")}
        </button>
      )}
    </div>
  )
}

/** A pane whose read failed after the page was on screen (paged review, C3-b2): an
 *  error with a retry, in place of the pane — never the page-wide error screen. */
function PaneError({
  testId,
  onRetry,
}: {
  testId: string
  onRetry?: () => void
}) {
  const { translate } = useTranslation()
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center"
      data-testid={testId}
      role="alert"
    >
      <p className="text-[13px] text-grey5">
        {translate("MC_DECL_V2_PANE_LOAD_ERROR")}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="sv2-btn sv2-btn-sm sv2-btn-primary"
        >
          {translate("MC_DECL_BTN_RETRY")}
        </button>
      )}
    </div>
  )
}

/**
 * Task 6 — the open-sibling hint (decision Ferdi 2026-09-04: option 2, no
 * automatic supersede), factored out (F9) so both the ordinary workbench
 * view and `AlreadyApprovedPanel`'s early-return share the same copy/keys/
 * count logic: a supplier with several uploaded declarations keeps
 * unapproved siblings in review forever after one is approved, and that is
 * exactly the declaration a reviewer opens FROM — so the already-approved
 * static panel needs this hint at least as much as the ordinary workbench
 * does. Needs a resolved supplier to link anywhere, which is also the one
 * case `open_sibling_count` is never anything but 0 — see `get_read_model`.
 *
 * `variant` is presentation-only (bug fix 2026-09-08): the call sites sit
 * in different visual contexts and need different tones, never a different
 * sentence: "bar" (default) is the full-width amber warn-toned strip; "card"
 * is `.sv2-siblinghint--card` in studioV2.css, nested inside
 * `AlreadyApprovedPanel`'s `.sv2-donecard`, the file's yellow
 * "informational" tone (matching `.sv2-donecard .sv2-st .sv2-n`/
 * `.sv2-freset`, not the destructive/warn amber a terminal "nothing left to
 * do" card has no business flashing) with an always-underlined link rather
 * than the bar's hover-only affordance. "inline" (H1, tester feedback) reuses
 * that same quiet card look for the ordinary workbench topbar, where it sits
 * as a direct child rather than inside a done card: it adds
 * `.sv2-siblinghint--inline` on top of `--card` to drop the card variant's
 * 24px bottom margin, which was sized for the space above `.sv2-donecard`,
 * not for the gap under the topbar.
 */

function OpenSiblingHint({
  supplierId,
  count,
  variant = "bar",
}: {
  supplierId: string | null | undefined
  count: number | undefined
  variant?: "bar" | "card" | "inline"
}) {
  const { translate } = useTranslation()
  if (!count || !supplierId) return null
  return (
    <Link
      to={openSiblingDeclarationsPath(supplierId)}
      className={cn(
        "sv2-siblinghint",
        (variant === "card" || variant === "inline") && "sv2-siblinghint--card",
        variant === "inline" && "sv2-siblinghint--inline",
      )}
    >
      {count === 1
        ? translate("MC_DECL_REVIEW_OPEN_SIBLING_ONE")
        : translate("MC_DECL_REVIEW_OPEN_SIBLINGS", { n: count })}
    </Link>
  )
}

// F10 — one lookup, not a chain of ternaries, so a fourth post-approval
// reading never has to be threaded through two separate `isX ? a : b` sites
// that could silently drift (the pill's CSS tone class and its label key).
const ALREADY_APPROVED_PILL: Record<
  "accepted" | "expiring" | "expired",
  { tone: "sv2-done" | "sv2-warn" | "sv2-fail"; labelKey: TranslationKeys }
> = {
  accepted: { tone: "sv2-done", labelKey: "MC_DECL_LC_ACCEPTED" },
  expiring: { tone: "sv2-warn", labelKey: "MC_DECL_LC_EXPIRING" },
  expired: { tone: "sv2-fail", labelKey: "MC_DECL_LIFECYCLE_EXPIRED" },
}

/**
 * B2 — the static terminal panel for a deep link into a declaration that is
 * already `accepted`/`expiring`/`expired`: not reachable from any in-shell
 * navigation (the list keeps such rows non-editable already), only from a
 * stored link, a bookmark, or Back into a since-accepted declaration.
 * Deliberately NOT `ApprovedView`: that component needs the POST approve
 * response payload (statement/substance write counts, an audit ref, …),
 * which does not exist outside the session that produced it — this panel
 * states the one fact that IS still true from a bare `lifecycleStatus` read,
 * nothing it cannot back.
 *
 * F9 — `openSiblingCount`/`supplierId` are optional (older/other call sites
 * keep working unset). The hint renders INSIDE `.sv2-donecard` (bug fix
 * 2026-09-08 — it used to sit as its own full-width bar above the card,
 * amber-toned and looking unclickable, detached from the card by the
 * vertically-centered panel's own top/bottom whitespace): the card is
 * already the terminal "nothing left to do here" message, and "but look
 * over there" reads as one more line in the same card rather than a stray
 * strip floating above it, in the file's yellow informational tone
 * (`variant="card"`, see `OpenSiblingHint`'s own doc) rather than the amber
 * warn tone the topbar bar keeps.
 */
function AlreadyApprovedPanel({
  lifecycleStatus,
  onBackToList,
  openSiblingCount,
  supplierId,
}: {
  lifecycleStatus: "accepted" | "expiring" | "expired"
  onBackToList: () => void
  openSiblingCount?: number
  supplierId?: string | null
}) {
  const { translate } = useTranslation()
  const pill = ALREADY_APPROVED_PILL[lifecycleStatus]
  return (
    <div className="sv2-root flex h-full min-h-0 flex-1 flex-col items-center justify-center bg-card px-6 text-center">
      <div className="sv2-donecard">
        <div className="sv2-ok">
          <CheckCircle2 size={28} className="sv2-ic" aria-hidden="true" />
        </div>
        <span className={cn("sv2-tpill", pill.tone, "mx-auto")}>
          <span className="sv2-d" />
          {translate(pill.labelKey)}
        </span>
        <h2>{translate("MC_DECL_V2_ALREADY_ACCEPTED_TITLE")}</h2>
        <p>{translate("MC_DECL_V2_ALREADY_ACCEPTED_BODY")}</p>
        <OpenSiblingHint
          supplierId={supplierId}
          count={openSiblingCount}
          variant="card"
        />
        <button
          type="button"
          className="sv2-btn sv2-btn-primary"
          onClick={onBackToList}
        >
          {translate("MC_DECL_V2_DONE_BACK_TO_LIST")}
        </button>
      </div>
    </div>
  )
}
